/**
 * 多店先付款页（`views/payment/index.vue`）建单没成时告诉顾客的那句话（后端 B18 跟改，owner 2026-10-26 定）。
 *
 * 按先后认四种（后端 `contactfree-backend` `CustomerControllers/DefaultController.php actionAddVenueSubGroupOrder`）：
 * 1. 有成员店暂停了顾客下单（B18，`api/Services/Order/OrderingGate.php subGroupCartRejection`）：`error.code === 'customerOrderingDisabled'`，
 *    `error.info.cartError = { <venueId>: { venue: [人话] } }`。回包里只有店 id，店名从组车存下的 `venueHome.subGroupVenueList` 取
 *    （组车页上显示的也是这个名字）。只认 `code`，不比对 message 原文（会被翻译）。
 * 2. 有商品卖完了 / 点不了（`SoldOutEnvelope::forSubGroupOrder`，message `Some Products Not Available`）：`cartError[店]` 是一串商品名 ——
 *    按形状认，同样不比对 message。顾客回组车要知道删哪几样，所以一行一个念出来（老站「菜单刚更新」弹窗也是这样列名字，`menu/menuChanged.js`）。
 * 3. 不知道建没建成（断网 / 超时 / 5xx / 处理回答的代码自己抛了，`utils/orderPlaced.js addOrderFailureOutcome` 的 unknown）：不说「没下成」，
 *    说「没能确认」。后端会复用这位顾客在这一组最近一张没付的父单（`SubGroupOrderAssembler`），所以回组车再下不会多出一张要付的单。
 * 4. 别的没建成（校验没过、回来的不是一张单 …）：一律同一句。
 *
 * 每句都说「还没扣款」：先付款页是一进来就建单，任何付款动作都在这之后。按钮只有一个：回组车
 * （组车重拉门店信息时会把暂停的那家标成没开、拿掉它的行，所以暂停那句不叫顾客自己去删）。
 */
import { t, tParams } from '@/utils/language'
import { ORDER_OUTCOME_UNKNOWN } from '@/utils/orderPlaced'

export const CUSTOMER_ORDERING_DISABLED = 'customerOrderingDisabled'

const has = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key)

/** 后端商品 id 的样子：`Tool::uuid()`（8-4-4-4-12 十六进制）或老数据的纯数字（与新顾客端 `addOrderResult.ts looksLikeProductId` 同口径） */
const PRODUCT_ID = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+)$/i

/** `cartError` 里第一家有内容的店（后端第一家被拦就回，实际只会有一家）；认不出 → '' */
function firstFilledVenueId(cartError) {
  if (!cartError || typeof cartError !== 'object' || Array.isArray(cartError)) return ''
  const venueId = Object.keys(cartError).find(id => {
    const errors = cartError[id]
    return id !== '' && errors !== null && errors !== undefined && errors !== '' && !(Array.isArray(errors) && errors.length === 0)
  })
  return venueId || ''
}

/** 这次的 `cartError`（拿不到 → null） */
function cartErrorOf(error) {
  const info = error && typeof error === 'object' && error.info && typeof error.info === 'object' ? error.info : null
  return info ? info.cartError : null
}

/** 暂停的是哪家店：不是暂停 ⇒ null；是暂停但认不出店 ⇒ '' */
function pausedVenueIdOf(error) {
  if (!error || typeof error !== 'object' || error.code !== CUSTOMER_ORDERING_DISABLED) return null
  return firstFilledVenueId(cartErrorOf(error))
}

/**
 * 卖完了 / 点不了的商品名（第一家有内容的店那一格是一串字符串才算；暂停 / 表单闸那一格是对象，不算）。
 * 找不到的商品后端写成 `名字|商品 id`（`OrderItemForm.php` notAvailableProducts）：最后一个 `|` 后面像商品 id 才拆，
 * 否则整串是名字（「Fish|Chips」）。去空、去重。
 */
function unavailableNamesOf(error) {
  const cartError = cartErrorOf(error)
  const venueId = firstFilledVenueId(cartError)
  const list = venueId ? cartError[venueId] : null
  if (!Array.isArray(list) || !list.every(item => typeof item === 'string')) return []
  const names = list.map(raw => {
    const bar = raw.lastIndexOf('|')
    return (bar >= 0 && PRODUCT_ID.test(raw.slice(bar + 1)) ? raw.slice(0, bar) : raw).trim()
  })
  return names.filter((name, index) => name && names.indexOf(name) === index)
}

/**
 * @param {*} error createOrder reject 出来的东西，或者回来的那个不是订单的回答
 * @param {Object} venueList `venueHome.subGroupVenueList`（店 id → 店）
 * @param {String} outcome `utils/orderPlaced.js` 的结果：没下成 / 不知道（reject 出来的按 addOrderFailureOutcome 判，回来的不是一张单 = 没下成）
 * @returns {{title: String, content: String, secContent: String, buttonText: String}}
 */
export function subGroupOrderFailureCopy(error, venueList, outcome) {
  const buttonText = t('back_to_cart', 'Back to cart')
  const pausedVenueId = pausedVenueIdOf(error)
  if (pausedVenueId !== null) {
    const venue = pausedVenueId && venueList && typeof venueList === 'object' && has(venueList, pausedVenueId) ? venueList[pausedVenueId] : null
    const name = venue && typeof venue.name === 'string' ? venue.name.trim() : ''
    return {
      // tParams 用 String.replace 换占位符：店名里的 `$$` / `$&` 会被当成替换记号，先转义
      title: name
        ? tParams('group_order_venue_paused', '{name} isn\'t taking orders right now', { name: name.replace(/\$/g, '$$$$') })
        : t('group_order_a_venue_paused', 'One of the venues isn\'t taking orders right now'),
      content: t('group_order_venue_paused_hint', 'You haven\'t been charged. Please go back to your cart and order from the other venues, or try again later.'),
      secContent: '',
      buttonText
    }
  }
  const unavailable = unavailableNamesOf(error)
  if (unavailable.length) {
    return {
      title: t('group_order_items_unavailable', 'Some items are no longer available'),
      content: unavailable.join('\n'),
      secContent: t('group_order_items_unavailable_hint', 'You haven\'t been charged. Please go back to your cart, remove these items and try again.'),
      buttonText
    }
  }
  if (outcome === ORDER_OUTCOME_UNKNOWN) {
    return {
      title: t('group_order_unconfirmed', 'We couldn\'t confirm your order'),
      content: t('group_order_unconfirmed_hint', 'You haven\'t been charged. Please check your internet connection, then go back to your cart and try again.'),
      secContent: '',
      buttonText
    }
  }
  return {
    title: t('group_order_failed', 'We couldn\'t place your order'),
    content: t('group_order_failed_hint', 'You haven\'t been charged. Please go back to your cart, check your items and try again.'),
    secContent: '',
    buttonText
  }
}
